import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Grid,
  Chip,
  Alert,
  Dialog,
  DialogContent,
  DialogActions,
  Divider,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Autocomplete,
  Avatar,
  Skeleton,
  IconButton,
  Tooltip,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
  AccessTime as AccessTimeIcon,
  DirectionsCar as DirectionsCarIcon,
  Build as BuildIcon,
  Construction as ConstructionIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Timer as TimerIcon,
  Speed as SpeedIcon,
  Notes as NotesIcon,
  Inventory as InventoryIcon,
  Draw as DrawIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Edit as EditIcon,
  PendingActions as PendingActionsIcon,
} from '@mui/icons-material';
import SignatureCanvas from 'react-signature-canvas';
import { generaRapportinoPDF } from '../utils/pdf';
import { PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { rapportiniService, clientiService, cantieriService } from '../services/api';
import RapportinoStandaloneModal from '../components/RapportinoStandaloneModal';

// Componente StatBox riutilizzabile
const StatBox = ({ icon, value, label }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      mb: 0.5
    }}>
      {icon && React.cloneElement(icon, { sx: { color: 'rgba(255,255,255,0.7)', fontSize: '1.2rem' } })}
      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
      {label}
    </Typography>
  </Box>
);

// Componente FormSection riutilizzabile
const FormSection = ({ icon, title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Avatar sx={{ bgcolor: 'rgba(229, 0, 0, 0.1)', color: '#e50000', width: 36, height: 36 }}>
        {icon}
      </Avatar>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

const Rapportini = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isCompact = useMediaQuery(theme.breakpoints.down('md')); // tablet/mobile: card invece di tabella larga
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();

  // State
  const [rapportini, setRapportini] = useState([]);
  const [daCompilare, setDaCompilare] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clienti, setClienti] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [tab, setTab] = useState(0); // 0 = compilati, 1 = da compilare

  // Filtri
  const [dataInizio, setDataInizio] = useState(dayjs().subtract(7, 'day'));
  const [dataFine, setDataFine] = useState(dayjs());
  const [clienteSelezionato, setClienteSelezionato] = useState(null);
  const [cantiereSelezionato, setCantiereSelezionato] = useState(null);
  const [utenteSelezionato, setUtenteSelezionato] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  // Modal dettaglio
  const [selectedRapportino, setSelectedRapportino] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Firma posticipata dialog
  const [firmaDialogOpen, setFirmaDialogOpen] = useState(false);
  const [firmaNome, setFirmaNome] = useState('');
  const [firmaLoading, setFirmaLoading] = useState(false);
  const [firmaError, setFirmaError] = useState('');
  const firmaSigCanvas = useRef(null);

  // Modal creazione standalone
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Paginazione
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [daCompilarePage, setDaCompilarePage] = useState(0);

  useEffect(() => {
    loadClienti();
    loadCantieri();
    loadUtenti();
    loadRapportini();
  }, []);

  const loadClienti = async () => {
    try {
      const response = await clientiService.getAll();
      setClienti(response.data);
    } catch (error) {
      console.error('Errore nel caricamento clienti:', error);
    }
  };

  const loadCantieri = async () => {
    try {
      const response = await cantieriService.getAll();
      setCantieri(response.data);
    } catch (error) {
      console.error('Errore nel caricamento cantieri:', error);
    }
  };

  const loadUtenti = async () => {
    try {
      const response = await rapportiniService.getUtenti();
      setUtenti(response.data);
    } catch (error) {
      console.error('Errore nel caricamento utenti:', error);
    }
  };

  // Accetta override espliciti per evitare di dipendere dallo stato asincrono
  // (es. quando si seleziona un utente e si azzerano le date nello stesso giro).
  const loadRapportini = async (overrides = {}) => {
    try {
      setLoading(true);
      setError('');

      const di = overrides.dataInizio !== undefined ? overrides.dataInizio : dataInizio;
      const df = overrides.dataFine !== undefined ? overrides.dataFine : dataFine;
      const cl = overrides.cliente !== undefined ? overrides.cliente : clienteSelezionato;
      const ca = overrides.cantiere !== undefined ? overrides.cantiere : cantiereSelezionato;
      const ut = overrides.utente !== undefined ? overrides.utente : utenteSelezionato;

      const params = {};
      if (di) {
        params.data_inizio = di.format('YYYY-MM-DD');
      }
      if (df) {
        params.data_fine = df.format('YYYY-MM-DD');
      }
      if (cl) {
        params.cliente_id = cl.id;
      }
      if (ca) {
        params.cantiere_id = ca.id;
      }
      if (ut) {
        params.utente_id = ut.id;
      }

      // I "da compilare" sono arretrati: si filtrano solo per cliente/utente,
      // non per data (vanno mostrati tutti a prescindere da quando sono).
      const paramsDaCompilare = {};
      if (cl) paramsDaCompilare.cliente_id = cl.id;
      if (ca) paramsDaCompilare.cantiere_id = ca.id;
      if (ut) paramsDaCompilare.utente_id = ut.id;

      const [response, respDaCompilare] = await Promise.all([
        rapportiniService.getAll(params),
        rapportiniService.getDaCompilare(paramsDaCompilare),
      ]);
      setRapportini(response.data);
      setDaCompilare(respDaCompilare.data);
      setDaCompilarePage(0);
    } catch (error) {
      console.error('Errore nel caricamento rapportini:', error);
      setError('Errore nel caricamento dei rapportini');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setPage(0);
    loadRapportini();
  };

  // Selezionando un utente si estraggono TUTTI i suoi rapportini: il vincolo di
  // data viene rimosso (poi l'admin può restringerlo se vuole). Deselezionando
  // si ripristina la finestra di default (ultimi 7 giorni).
  const handleUtenteChange = (event, newValue) => {
    setUtenteSelezionato(newValue);
    setPage(0);
    if (newValue) {
      setDataInizio(null);
      setDataFine(null);
      loadRapportini({ utente: newValue, dataInizio: null, dataFine: null });
    } else {
      const di = dayjs().subtract(7, 'day');
      const df = dayjs();
      setDataInizio(di);
      setDataFine(df);
      loadRapportini({ utente: null, dataInizio: di, dataFine: df });
    }
  };

  // Cantieri proposti nel filtro: se c'è un cliente selezionato mostro solo i suoi.
  const cantieriDisponibili = useMemo(
    () =>
      clienteSelezionato
        ? cantieri.filter((c) => c.cliente_id === clienteSelezionato.id)
        : cantieri,
    [cantieri, clienteSelezionato]
  );

  // Cambiando cliente, un cantiere già scelto che non gli appartiene va tolto:
  // altrimenti i due filtri si contraddicono e la lista esce vuota senza motivo.
  const handleClienteChange = (event, newValue) => {
    setClienteSelezionato(newValue);
    setPage(0);
    if (
      cantiereSelezionato &&
      newValue &&
      cantiereSelezionato.cliente_id !== newValue.id
    ) {
      setCantiereSelezionato(null);
    }
  };

  const handleResetFilters = () => {
    const di = dayjs().subtract(7, 'day');
    const df = dayjs();
    setDataInizio(di);
    setDataFine(df);
    setClienteSelezionato(null);
    setCantiereSelezionato(null);
    setUtenteSelezionato(null);
    setPage(0);
    loadRapportini({ dataInizio: di, dataFine: df, cliente: null, cantiere: null, utente: null });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setDaCompilarePage(0);
  };

  const paginatedRapportini = rapportini.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const paginatedDaCompilare = daCompilare.slice(
    daCompilarePage * rowsPerPage,
    daCompilarePage * rowsPerPage + rowsPerPage
  );

  const handleChangeDaCompilarePage = (event, newPage) => setDaCompilarePage(newPage);

  const handleTabChange = (event, newValue) => setTab(newValue);

  // Apre il dettaglio dell'intervento, da cui si compila il rapportino mancante
  const handleCompila = (intervento) => {
    navigate(`/interventi/${intervento.id}`);
  };

  const handleViewDetail = (rapportino) => {
    setSelectedRapportino(rapportino);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedRapportino(null);
  };

  const handleOpenFirmaDialog = () => {
    setFirmaNome('');
    setFirmaDialogOpen(true);
  };

  const handleCloseFirmaDialog = () => {
    setFirmaDialogOpen(false);
    setFirmaNome('');
    setFirmaError('');
    if (firmaSigCanvas.current) {
      firmaSigCanvas.current.clear();
    }
  };

  const handleSaveFirma = async () => {
    if (!firmaSigCanvas.current || firmaSigCanvas.current.isEmpty()) {
      setFirmaError('Disegna la firma nel riquadro prima di salvare.');
      return;
    }
    if (!firmaNome.trim()) {
      setFirmaError('Inserisci il nome del firmatario.');
      return;
    }
    setFirmaError('');
    try {
      setFirmaLoading(true);
      const firmaData = {
        firma_cliente: firmaSigCanvas.current.toDataURL('image/png'),
        nome_firmatario: firmaNome,
      };
      const response = await rapportiniService.update(selectedRapportino.id, firmaData);
      setSelectedRapportino(response.data);
      loadRapportini();
      handleCloseFirmaDialog();
    } catch (err) {
      console.error('Errore nel salvataggio firma:', err);
      if (err.isOffline && err.queued) {
        // Firma salvata nella coda offline: chiudi (il toast globale avvisa, sincronizza dopo)
        handleCloseFirmaDialog();
        return;
      }
      setFirmaError(err.isOffline ? err.message : (err.response?.data?.message || 'Errore nel salvataggio della firma. Riprova.'));
    } finally {
      setFirmaLoading(false);
    }
  };

  const getTipologiaStyle = (tipologia) => {
    return tipologia === 'manutenzione'
      ? { bg: 'rgba(229, 0, 0, 0.1)', color: '#e50000', icon: <BuildIcon sx={{ fontSize: '14px !important' }} /> }
      : { bg: 'rgba(156, 39, 176, 0.1)', color: '#9c27b0', icon: <ConstructionIcon sx={{ fontSize: '14px !important' }} /> };
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImage = (contentType) => {
    return contentType && contentType.startsWith('image/');
  };

  // Formatta orario da "HH:MM:SS" a "HH:MM"
  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    return timeStr.substring(0, 5);
  };

  // Minuti fra due orari "HH:MM[:SS]" della stessa giornata (gestisce la mezzanotte).
  const minutiTraOrari = (inizio, fine) => {
    const aMinuti = (o) => {
      if (!o) return null;
      const p = String(o).split(':');
      if (p.length < 2) return null;
      const h = Number(p[0]);
      const m = Number(p[1]);
      return isNaN(h) || isNaN(m) ? null : h * 60 + m;
    };
    const a = aMinuti(inizio);
    const b = aMinuti(fine);
    if (a === null || b === null) return null;
    const d = b - a;
    return d < 0 ? d + 24 * 60 : d;
  };

  // "45 min" oppure "1h 30m". Lo zero è un valore legittimo (partenza e arrivo
  // timbrati nello stesso minuto) e va mostrato, non nascosto dietro un trattino.
  const formatDurata = (minuti) => {
    if (minuti === null || minuti === undefined) return '-';
    const v = Number(minuti);
    if (!isFinite(v) || isNaN(v) || v < 0) return '-';
    const tot = Math.round(v);
    if (tot < 60) return `${tot} min`;
    const h = Math.floor(tot / 60);
    const m = tot % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  };

  // Dati del rapportino aperto nel dettaglio: pausa e durate di viaggio.
  // Dove ci sono le timbrature uso la durata REALE, altrimenti la stima salvata
  // sull'intervento (e lo dichiaro sotto al valore, così il dato è interpretabile).
  const minutiPausaSel =
    selectedRapportino?.minuti_pausa != null
      ? Number(selectedRapportino.minuti_pausa)
      : minutiTraOrari(selectedRapportino?.ora_pausa_inizio, selectedRapportino?.ora_pausa_fine) || 0;

  const viaggioAndataRealeSel = minutiTraOrari(
    selectedRapportino?.ora_partenza_sede,
    selectedRapportino?.ora_arrivo_cantiere
  );
  const viaggioRitornoRealeSel = minutiTraOrari(
    selectedRapportino?.ora_partenza_cantiere,
    selectedRapportino?.ora_arrivo_sede
  );
  const viaggioAndataSel =
    viaggioAndataRealeSel != null ? viaggioAndataRealeSel : selectedRapportino?.tempo_viaggio_andata ?? null;
  const viaggioRitornoSel =
    viaggioRitornoRealeSel != null ? viaggioRitornoRealeSel : selectedRapportino?.tempo_viaggio_ritorno ?? null;

  const handleDownloadAllegato = (allegato) => {
    const link = document.createElement('a');
    link.href = allegato.data_base64;
    link.download = allegato.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Statistiche
  const stats = {
    totali: rapportini.length,
    oreTotali: rapportini.reduce((acc, r) => acc + (r.ore_lavorate || 0), 0).toFixed(1),
    kmTotali: rapportini.reduce((acc, r) => acc + (r.km_percorsi || 0), 0).toFixed(0),
    conAllegati: rapportini.filter(r => r.allegati && r.allegati.length > 0).length,
  };

  // Verifica permessi
  if (user?.role !== 'admin' && user?.role !== 'amministratore' && user?.role !== 'super_admin' && user?.role !== 'capocantiere') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Non hai i permessi per accedere a questa pagina
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Skeleton variant="rounded" height={200} sx={{ mb: 3, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={120} sx={{ mb: 3, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={400} sx={{ borderRadius: 2 }} />
      </Container>
    );
  }

  return (
    <Box sx={{ pb: { xs: 10, sm: 4 } }}>
      {/* Header moderno con sfondo scuro */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          color: 'white',
          pt: { xs: 2, sm: 4 },
          pb: { xs: 3, sm: 4 },
          px: { xs: 2, sm: 3 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Pattern decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Accento rosso decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            background: 'radial-gradient(circle, rgba(229,0,0,0.15) 0%, transparent 70%)',
            borderRadius: '50%',
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Titolo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar
              sx={{
                width: { xs: 48, sm: 56 },
                height: { xs: 48, sm: 56 },
                bgcolor: '#e50000',
                boxShadow: '0 4px 14px rgba(229,0,0,0.4)',
              }}
            >
              <DescriptionIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  letterSpacing: '-0.5px',
                }}
              >
                {isMobile ? 'Rapportini' : 'Rapportini di Lavoro'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                Gestione e consultazione rapportini
              </Typography>
            </Box>
            {/* I rapportini si creano dagli interventi (timbrature → "Compila rapportino"),
                quindi non c'è più la creazione "standalone" da questa pagina. */}
          </Box>

          {/* Statistiche */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: { xs: 2, sm: 3 },
              mt: 3,
            }}
          >
            <StatBox
              icon={<DescriptionIcon />}
              value={stats.totali}
              label="Rapportini"
            />
            <StatBox
              icon={<TimerIcon />}
              value={stats.oreTotali}
              label="Ore Totali"
            />
            <StatBox
              icon={<SpeedIcon />}
              value={stats.kmTotali}
              label="Km Totali"
            />
            <StatBox
              icon={<AttachFileIcon />}
              value={stats.conAllegati}
              label="Con Allegati"
            />
          </Box>
        </Container>
      </Box>

      {/* Contenuto principale */}
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Filtri */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: showFilters ? 2.5 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: 'rgba(229, 0, 0, 0.1)', color: '#e50000', width: 36, height: 36 }}>
                <FilterListIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                Filtri di Ricerca
              </Typography>
            </Box>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              {showFilters ? <CloseIcon /> : <FilterListIcon />}
            </IconButton>
          </Box>

          {showFilters && (
            <Grid container spacing={2} alignItems="flex-end">
              {/* md: due righe (3+3+6 / 6+6) così ogni etichetta ha spazio;
                  lg: tutto su una riga. Con md={2} "Persona coinvolta" veniva
                  tagliata di 42px in tablet orizzontale. */}
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <DatePicker
                  label="Data Inizio"
                  value={dataInizio}
                  onChange={(newValue) => setDataInizio(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3} lg={2}>
                <DatePicker
                  label="Data Fine"
                  value={dataFine}
                  onChange={(newValue) => setDataFine(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={3}>
                <Autocomplete
                  options={clienti}
                  getOptionLabel={(option) => option.ragione_sociale}
                  value={clienteSelezionato}
                  onChange={handleClienteChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      size="small"
                      placeholder="Tutti i clienti"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>

              {/* Filtro per commessa/cantiere: le opzioni si restringono al cliente
                  scelto, così su 81 cantieri si arriva subito a quello giusto. */}
              <Grid item xs={12} sm={6} md={6} lg={3}>
                <Autocomplete
                  options={cantieriDisponibili}
                  getOptionLabel={(option) =>
                    option.codice ? `${option.codice} · ${option.nome}` : option.nome || ''
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={cantiereSelezionato}
                  onChange={(event, newValue) => setCantiereSelezionato(newValue)}
                  noOptionsText={
                    clienteSelezionato ? 'Nessun cantiere per questo cliente' : 'Nessun cantiere'
                  }
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id} sx={{ display: 'block !important', py: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {option.nome}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {option.codice}
                        {option.cliente ? ` · ${option.cliente}` : ''}
                      </Typography>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Commessa / Cantiere"
                      size="small"
                      placeholder={
                        clienteSelezionato ? 'Tutti i cantieri del cliente' : 'Tutte le commesse'
                      }
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={2}>
                <Autocomplete
                  options={utenti}
                  getOptionLabel={(option) => option.nome || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={utenteSelezionato}
                  onChange={handleUtenteChange}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <PersonIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />
                      {option.nome}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Persona coinvolta"
                      size="small"
                      placeholder="Tutti gli utenti"
                      helperText={utenteSelezionato ? 'Tutti i rapportini in cui è coinvolto' : ''}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={1} justifyContent={{ xs: 'stretch', sm: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    sx={{
                      bgcolor: '#e50000',
                      borderRadius: 2,
                      fontWeight: 600,
                      px: 4,
                      flex: { xs: 1, sm: 'none' },
                      '&:hover': { bgcolor: '#c50000' },
                    }}
                  >
                    Applica
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleResetFilters}
                    startIcon={<ClearIcon />}
                    sx={{
                      borderRadius: 2,
                      borderColor: 'divider',
                      color: 'text.secondary',
                      '&:hover': { borderColor: '#e50000', color: '#e50000' },
                    }}
                  >
                    Reset
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </Paper>

        {/* Tab: Compilati / Da compilare */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', fontSize: '0.95rem', minHeight: 48 },
              '& .Mui-selected': { color: '#e50000 !important' },
              '& .MuiTabs-indicator': { backgroundColor: '#e50000' },
            }}
          >
            <Tab
              icon={<DescriptionIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Compilati
                  <Chip label={rapportini.length} size="small" sx={{ height: 20, fontWeight: 700, bgcolor: 'grey.200' }} />
                </Box>
              }
            />
            <Tab
              icon={<PendingActionsIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Da compilare
                  <Chip
                    label={daCompilare.length}
                    size="small"
                    sx={{
                      height: 20,
                      fontWeight: 700,
                      bgcolor: daCompilare.length > 0 ? '#ff9800' : 'grey.200',
                      color: daCompilare.length > 0 ? 'white' : 'inherit',
                    }}
                  />
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Sezione: Rapportini compilati */}
        {tab === 0 && (
          <>
        {/* Risultati */}
        {rapportini.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nessun rapportino trovato
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Prova a modificare i criteri di ricerca
            </Typography>
          </Paper>
        ) : isCompact ? (
          /* Mobile Cards */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {paginatedRapportini.map((rapportino) => {
              const tipologiaStyle = getTipologiaStyle(rapportino.intervento?.tipologia);

              return (
                <Card
                  key={rapportino.id}
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#e50000',
                      boxShadow: '0 8px 24px rgba(229,0,0,0.12)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    {/* Header card */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                          {rapportino.cliente_nome}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {rapportino.intervento?.cantiere_nome}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {rapportino.creatore_nome || '-'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                        <Chip
                          icon={tipologiaStyle.icon}
                          label={rapportino.intervento?.tipologia || '-'}
                          size="small"
                          sx={{
                            bgcolor: tipologiaStyle.bg,
                            color: tipologiaStyle.color,
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            '& .MuiChip-icon': { color: tipologiaStyle.color },
                          }}
                        />
                        {rapportino.intervento?.tipologia === 'manutenzione' && !rapportino.firma_cliente && (
                          <Chip
                            icon={<WarningIcon sx={{ fontSize: '12px !important' }} />}
                            label="Da firmare"
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255, 152, 0, 0.1)',
                              color: '#ff9800',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              '& .MuiChip-icon': { color: '#ff9800' },
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Info */}
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {rapportino.data
                              ? new Date(rapportino.data).toLocaleDateString('it-IT')
                              : '-'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimerIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {rapportino.ore_lavorate
                              ? `${rapportino.ore_lavorate.toFixed(1)} h`
                              : '-'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <DirectionsCarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {rapportino.km_percorsi
                              ? `${rapportino.km_percorsi.toFixed(0)} km`
                              : '-'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {rapportino.allegati && rapportino.allegati.length > 0
                              ? `${rapportino.allegati.length} file`
                              : 'Nessuno'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Bottone */}
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewDetail(rapportino)}
                      fullWidth
                      sx={{
                        borderRadius: 2,
                        borderColor: '#e50000',
                        color: '#e50000',
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: '#c50000',
                          bgcolor: 'rgba(229, 0, 0, 0.05)',
                        },
                      }}
                    >
                      Visualizza Dettagli
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          /* Desktop Table */
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflowX: 'auto',
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Cantiere</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Capocantiere</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Tipologia</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Ore</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Km</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Allegati</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Firma</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRapportini.map((rapportino) => {
                  const tipologiaStyle = getTipologiaStyle(rapportino.intervento?.tipologia);

                  return (
                    <TableRow
                      key={rapportino.id}
                      sx={{
                        '&:hover': { bgcolor: 'rgba(229, 0, 0, 0.02)' },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarIcon sx={{ fontSize: 18, color: '#e50000' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {rapportino.data
                              ? new Date(rapportino.data).toLocaleDateString('it-IT')
                              : '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, bgcolor: 'grey.100', color: 'text.secondary' }}>
                            <BusinessIcon sx={{ fontSize: 16 }} />
                          </Avatar>
                          <Typography variant="body2">{rapportino.cliente_nome || '-'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {rapportino.intervento?.cantiere_nome || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {rapportino.creatore_nome || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={tipologiaStyle.icon}
                          label={rapportino.intervento?.tipologia || '-'}
                          size="small"
                          sx={{
                            bgcolor: tipologiaStyle.bg,
                            color: tipologiaStyle.color,
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            '& .MuiChip-icon': { color: tipologiaStyle.color },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={rapportino.ore_lavorate ? `${rapportino.ore_lavorate.toFixed(1)} h` : '-'}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(33, 150, 243, 0.1)',
                            color: '#2196f3',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <DirectionsCarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {rapportino.km_percorsi ? `${rapportino.km_percorsi.toFixed(0)} km` : '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {rapportino.allegati && rapportino.allegati.length > 0 ? (
                          <Chip
                            icon={<AttachFileIcon sx={{ fontSize: '14px !important' }} />}
                            label={rapportino.allegati.length}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(76, 175, 80, 0.1)',
                              color: '#4caf50',
                              fontWeight: 600,
                              '& .MuiChip-icon': { color: '#4caf50' },
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.disabled">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {rapportino.intervento?.tipologia === 'manutenzione' ? (
                          rapportino.firma_cliente ? (
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                              label="Firmato"
                              size="small"
                              sx={{
                                bgcolor: 'rgba(76, 175, 80, 0.1)',
                                color: '#4caf50',
                                fontWeight: 600,
                                '& .MuiChip-icon': { color: '#4caf50' },
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<WarningIcon sx={{ fontSize: '14px !important' }} />}
                              label="Da firmare"
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255, 152, 0, 0.1)',
                                color: '#ff9800',
                                fontWeight: 600,
                                '& .MuiChip-icon': { color: '#ff9800' },
                              }}
                            />
                          )
                        ) : (
                          <Typography variant="body2" color="text.disabled">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Stampa PDF">
                          <IconButton
                            size="small"
                            onClick={() => generaRapportinoPDF(rapportino)}
                            sx={{ color: '#555', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }}
                          >
                            <PictureAsPdfIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Visualizza dettagli">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetail(rapportino)}
                            sx={{
                              color: '#e50000',
                              '&:hover': { bgcolor: 'rgba(229, 0, 0, 0.1)' },
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Paginazione */}
        {rapportini.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <TablePagination
              component="div"
              count={rapportini.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Righe per pagina:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
              sx={{
                bgcolor: '#fafafa',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.875rem',
                },
              }}
            />
          </Paper>
        )}
          </>
        )}

        {/* Sezione: Rapportini da compilare (interventi conclusi senza rapportino) */}
        {tab === 1 && (
          <>
            {daCompilare.length === 0 ? (
              <Paper
                elevation={0}
                sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
              >
                <CheckCircleIcon sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nessun rapportino da compilare
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Tutti gli interventi conclusi hanno già il rapportino
                </Typography>
              </Paper>
            ) : isCompact ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {paginatedDaCompilare.map((intervento) => {
                  const tipologiaStyle = getTipologiaStyle(intervento.tipologia);
                  return (
                    <Card key={intervento.id} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                              {intervento.cliente || '-'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {intervento.cantiere || '-'}
                            </Typography>
                          </Box>
                          <Chip
                            icon={tipologiaStyle.icon}
                            label={intervento.tipologia || '-'}
                            size="small"
                            sx={{
                              bgcolor: tipologiaStyle.bg,
                              color: tipologiaStyle.color,
                              fontWeight: 600,
                              textTransform: 'capitalize',
                              '& .MuiChip-icon': { color: tipologiaStyle.color },
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {intervento.data ? new Date(intervento.data).toLocaleDateString('it-IT') : '-'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                          <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {intervento.capocantiere || '-'}
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<EditIcon />}
                          onClick={() => handleCompila(intervento)}
                          sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' }, borderRadius: 2, fontWeight: 700 }}
                        >
                          Compila Rapportino
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Data</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Cliente</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Cantiere</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Capocantiere</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Tipologia</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>Azione</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedDaCompilare.map((intervento) => {
                      const tipologiaStyle = getTipologiaStyle(intervento.tipologia);
                      return (
                        <TableRow key={intervento.id} sx={{ '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.04)' }, transition: 'background-color 0.2s' }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarIcon sx={{ fontSize: 18, color: '#ff9800' }} />
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {intervento.data ? new Date(intervento.data).toLocaleDateString('it-IT') : '-'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 28, height: 28, bgcolor: 'grey.100', color: 'text.secondary' }}>
                                <BusinessIcon sx={{ fontSize: 16 }} />
                              </Avatar>
                              <Typography variant="body2">{intervento.cliente || '-'}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">{intervento.cantiere || '-'}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">{intervento.capocantiere || '-'}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={tipologiaStyle.icon}
                              label={intervento.tipologia || '-'}
                              size="small"
                              sx={{
                                bgcolor: tipologiaStyle.bg,
                                color: tipologiaStyle.color,
                                fontWeight: 600,
                                textTransform: 'capitalize',
                                '& .MuiChip-icon': { color: tipologiaStyle.color },
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleCompila(intervento)}
                              sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' }, borderRadius: 2, fontWeight: 700, whiteSpace: 'nowrap' }}
                            >
                              Compila
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {daCompilare.length > 0 && (
              <Paper
                elevation={0}
                sx={{ mt: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
              >
                <TablePagination
                  component="div"
                  count={daCompilare.length}
                  page={daCompilarePage}
                  onPageChange={handleChangeDaCompilarePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                  labelRowsPerPage="Righe per pagina:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
                  sx={{ bgcolor: '#fafafa', '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.875rem' } }}
                />
              </Paper>
            )}
          </>
        )}
      </Container>

      {/* Modal Dettaglio Rapportino */}
      <Dialog
        open={detailOpen}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
          },
        }}
      >
        {selectedRapportino && (
          <>
            {/* Header Dialog */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                color: 'white',
                p: 3,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: '#e50000', width: 48, height: 48 }}>
                    <DescriptionIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {selectedRapportino.cliente_nome}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                      {selectedRapportino.cantiere_nome || selectedRapportino.intervento?.cantiere_nome}
                    </Typography>
                    {/* Codice commessa: era nel database ma non veniva mai mostrato */}
                    {selectedRapportino.cantiere_codice && (
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.65, fontFamily: 'monospace', letterSpacing: 0.5 }}
                      >
                        Commessa {selectedRapportino.cantiere_codice}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <IconButton onClick={handleCloseDetail} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Info rapide header */}
              <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {selectedRapportino.data
                      ? new Date(selectedRapportino.data).toLocaleDateString('it-IT')
                      : '-'}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  icon={getTipologiaStyle(selectedRapportino.intervento?.tipologia).icon}
                  label={selectedRapportino.intervento?.tipologia}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    textTransform: 'capitalize',
                    '& .MuiChip-icon': { color: 'white' },
                  }}
                />
              </Box>
            </Box>

            <DialogContent sx={{ p: 3 }}>
              {/* Orari e Tempi */}
              <FormSection icon={<AccessTimeIcon sx={{ fontSize: 20 }} />} title="Orari e Tempi">
                {/* Sequenza completa della giornata, in ordine cronologico.
                    Prima "Arrivo cantiere" e "Inizio lavoro" erano fusi in un unico
                    riquadro (pur essendo due timbrature distinte) e la PAUSA non
                    compariva affatto: le ore totali sembravano non tornare. */}
                <Grid container spacing={2}>
                  {[
                    { label: 'Partenza Sede', valore: selectedRapportino.ora_partenza_sede, colore: '#1976d2', sfondo: 'rgba(33, 150, 243, 0.08)', bordo: 'rgba(33, 150, 243, 0.25)' },
                    { label: 'Arrivo Cantiere', valore: selectedRapportino.ora_arrivo_cantiere, colore: '#4caf50', sfondo: 'rgba(76, 175, 80, 0.1)', bordo: 'rgba(76, 175, 80, 0.3)' },
                    { label: 'Inizio Lavoro', valore: selectedRapportino.ora_inizio_lavoro, colore: '#4caf50', sfondo: 'rgba(76, 175, 80, 0.1)', bordo: 'rgba(76, 175, 80, 0.3)' },
                    { label: 'Inizio Pausa', valore: selectedRapportino.ora_pausa_inizio, colore: '#6d4c41', sfondo: 'rgba(121, 85, 72, 0.08)', bordo: 'rgba(121, 85, 72, 0.25)' },
                    { label: 'Fine Pausa', valore: selectedRapportino.ora_pausa_fine, colore: '#6d4c41', sfondo: 'rgba(121, 85, 72, 0.08)', bordo: 'rgba(121, 85, 72, 0.25)' },
                    { label: 'Fine Lavoro', valore: selectedRapportino.ora_fine_lavoro, colore: '#f44336', sfondo: 'rgba(244, 67, 54, 0.1)', bordo: 'rgba(244, 67, 54, 0.3)' },
                    { label: 'Partenza Cantiere', valore: selectedRapportino.ora_partenza_cantiere, colore: '#e65100', sfondo: 'rgba(255, 152, 0, 0.08)', bordo: 'rgba(255, 152, 0, 0.25)' },
                    { label: 'Arrivo Sede', valore: selectedRapportino.ora_arrivo_sede, colore: '#e65100', sfondo: 'rgba(255, 152, 0, 0.08)', bordo: 'rgba(255, 152, 0, 0.25)' },
                  ]
                    // Mostro solo le fasi realmente timbrate: un riquadro con "-" non
                    // aggiunge nulla e allunga la finestra su telefono.
                    .filter((f) => f.valore)
                    .map((f) => (
                      <Grid item xs={6} sm={4} md={3} key={f.label}>
                        <Paper
                          elevation={0}
                          sx={{ p: 1.5, bgcolor: f.sfondo, borderRadius: 2, textAlign: 'center', border: `1px solid ${f.bordo}`, height: '100%' }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                            {f.label}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: f.colore }}>
                            {formatTime(f.valore)}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                </Grid>

                {/* Riepilogo ore, km e tempi viaggio */}
                <Box sx={{ mt: 2, p: 2.5, bgcolor: 'rgba(229, 0, 0, 0.05)', borderRadius: 2, border: '1px solid rgba(229, 0, 0, 0.2)' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={2.4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Ore Lavorate</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#e50000' }}>
                          {selectedRapportino.ore_lavorate != null ? `${selectedRapportino.ore_lavorate.toFixed(1)} h` : '-'}
                        </Typography>
                        {minutiPausaSel > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            al netto della pausa
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    {/* La pausa non compariva da nessuna parte pur essendo timbrata
                        e già sottratta dalle ore lavorate. */}
                    <Grid item xs={6} sm={4} md={2.4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Pausa</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#6d4c41' }}>
                          {minutiPausaSel > 0 ? formatDurata(minutiPausaSel) : '-'}
                        </Typography>
                        {minutiPausaSel > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {formatTime(selectedRapportino.ora_pausa_inizio)}–{formatTime(selectedRapportino.ora_pausa_fine)}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Km Percorsi</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <DirectionsCarIcon sx={{ color: '#e50000' }} />
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#e50000' }}>
                            {selectedRapportino.km_percorsi ? `${selectedRapportino.km_percorsi.toFixed(0)}` : '-'}
                          </Typography>
                        </Box>
                        {selectedRapportino.km_percorsi > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            andata + ritorno
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Viaggio Andata</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          {viaggioAndataSel != null ? formatDurata(viaggioAndataSel) : '-'}
                        </Typography>
                        {viaggioAndataSel != null && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {viaggioAndataRealeSel != null ? 'da timbrature' : 'stimato'}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2.4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Viaggio Ritorno</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#e65100' }}>
                          {viaggioRitornoSel != null ? formatDurata(viaggioRitornoSel) : '-'}
                        </Typography>
                        {viaggioRitornoSel != null && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {viaggioRitornoRealeSel != null ? 'da timbrature' : 'stimato'}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Dettaglio ore per tecnico */}
                {selectedRapportino.ore_per_tecnico && selectedRapportino.ore_per_tecnico.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Ore per Tecnico</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Tecnico</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Ore</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedRapportino.ore_per_tecnico.map((t, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{t.nome}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={`${t.ore} h`}
                                  size="small"
                                  sx={{ fontWeight: 700, bgcolor: 'rgba(229, 0, 0, 0.1)', color: '#e50000' }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </FormSection>

              <Divider sx={{ my: 3 }} />

              {/* Descrizione */}
              {selectedRapportino.descrizione_intervento && (
                <FormSection icon={<NotesIcon sx={{ fontSize: 20 }} />} title="Descrizione Lavori">
                  <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedRapportino.descrizione_intervento}
                    </Typography>
                  </Paper>
                </FormSection>
              )}

              {/* Materiali */}
              {selectedRapportino.materiali_utilizzati && (
                <FormSection icon={<InventoryIcon sx={{ fontSize: 20 }} />} title="Materiali Utilizzati">
                  <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedRapportino.materiali_utilizzati}
                    </Typography>
                  </Paper>
                </FormSection>
              )}

              {/* Note */}
              {selectedRapportino.note && (
                <FormSection icon={<AssignmentIcon sx={{ fontSize: 20 }} />} title="Note">
                  <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'rgba(255, 152, 0, 0.08)', borderRadius: 2, border: '1px solid rgba(255, 152, 0, 0.2)' }}>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedRapportino.note}
                    </Typography>
                  </Paper>
                </FormSection>
              )}

              {/* Allegati */}
              {selectedRapportino.allegati && selectedRapportino.allegati.length > 0 && (
                <FormSection icon={<AttachFileIcon sx={{ fontSize: 20 }} />} title={`Allegati (${selectedRapportino.allegati.length})`}>
                  <Box display="flex" flexDirection="column" gap={1.5}>
                    {selectedRapportino.allegati.map((allegato, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: '#e50000',
                            bgcolor: 'rgba(229, 0, 0, 0.02)',
                          },
                        }}
                      >
                        {/* Preview */}
                        {isImage(allegato.content_type) ? (
                          <Box
                            component="img"
                            src={allegato.data_base64}
                            alt={allegato.filename}
                            sx={{
                              width: 70,
                              height: 70,
                              objectFit: 'cover',
                              borderRadius: 2,
                              border: '1px solid #e0e0e0',
                              cursor: 'pointer',
                            }}
                            onClick={() => window.open(allegato.data_base64, '_blank')}
                          />
                        ) : (
                          <Avatar sx={{ width: 70, height: 70, bgcolor: 'grey.100', borderRadius: 2 }}>
                            <InsertDriveFileIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                          </Avatar>
                        )}

                        {/* Info */}
                        <Box flexGrow={1}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {allegato.filename}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(allegato.size)}
                          </Typography>
                        </Box>

                        {/* Download */}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadAllegato(allegato)}
                          sx={{
                            borderRadius: 2,
                            borderColor: '#e50000',
                            color: '#e50000',
                            '&:hover': {
                              borderColor: '#c50000',
                              bgcolor: 'rgba(229, 0, 0, 0.05)',
                            },
                          }}
                        >
                          Scarica
                        </Button>
                      </Paper>
                    ))}
                  </Box>
                </FormSection>
              )}

              {/* Firma */}
              {selectedRapportino.firma_cliente ? (
                <FormSection icon={<DrawIcon sx={{ fontSize: 20 }} />} title="Firma Cliente">
                  <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        <strong>Firmatario:</strong> {selectedRapportino.nome_firmatario}
                      </Typography>
                    </Box>
                    <Box
                      component="img"
                      src={selectedRapportino.firma_cliente}
                      alt="Firma cliente"
                      sx={{
                        maxWidth: '100%',
                        height: 'auto',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        bgcolor: 'white',
                      }}
                    />
                  </Paper>
                </FormSection>
              ) : selectedRapportino.intervento?.tipologia === 'manutenzione' && (
                <FormSection icon={<DrawIcon sx={{ fontSize: 20 }} />} title="Firma Cliente">
                  <Alert
                    severity="warning"
                    icon={<WarningIcon />}
                    sx={{ mb: 2, borderRadius: 2 }}
                  >
                    Firma mancante — responsabile assente al momento della compilazione
                  </Alert>
                  {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'capocantiere') && (
                    <Button
                      variant="contained"
                      startIcon={<DrawIcon />}
                      onClick={handleOpenFirmaDialog}
                      sx={{
                        bgcolor: '#ff9800',
                        '&:hover': { bgcolor: '#f57c00' },
                        borderRadius: 2,
                        fontWeight: 600,
                      }}
                    >
                      Raccogli Firma
                    </Button>
                  )}
                </FormSection>
              )}

              {/* Task Svolte */}
              {selectedRapportino.tasks_svolte && selectedRapportino.tasks_svolte.length > 0 && (
                <FormSection icon={<CheckCircleIcon sx={{ fontSize: 20 }} />} title={`Task Svolte (${selectedRapportino.tasks_svolte.length})`}>
                  <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Descrizione</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Quantità</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedRapportino.tasks_svolte.map((task, index) => (
                          <TableRow key={index}>
                            <TableCell>{task.descrizione || '-'}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={task.quantita_oggi || task.quantita_completata || '-'}
                                size="small"
                                sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#4caf50', fontWeight: 600 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </FormSection>
              )}

              {/* Info Creazione */}
              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                <PersonIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">
                  Creato da <strong>{selectedRapportino.creatore_nome}</strong> il{' '}
                  {selectedRapportino.creato_il
                    ? new Date(selectedRapportino.creato_il).toLocaleString('it-IT')
                    : '-'}
                </Typography>
              </Box>

              {selectedRapportino.partecipanti && selectedRapportino.partecipanti.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Persone coinvolte:
                  </Typography>
                  {selectedRapportino.partecipanti.map((p) => (
                    <Chip
                      key={p.user_id}
                      icon={<PersonIcon sx={{ fontSize: '14px !important' }} />}
                      label={p.nome}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  ))}
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
              {/* Il PDF si poteva scaricare solo dalla lista: da qui, dopo aver
                  controllato i dati, mancava. */}
              <Button
                onClick={() => generaRapportinoPDF(selectedRapportino)}
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                sx={{ borderRadius: 2, px: 3, fontWeight: 600 }}
              >
                Scarica PDF
              </Button>
              <Button
                onClick={handleCloseDetail}
                variant="contained"
                sx={{
                  bgcolor: '#e50000',
                  borderRadius: 2,
                  px: 4,
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#c50000' },
                }}
              >
                Chiudi
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog Firma Posticipata */}
      <Dialog
        open={firmaDialogOpen}
        onClose={handleCloseFirmaDialog}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            <DrawIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Raccogli Firma Cliente
          </Typography>
          {firmaError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFirmaError('')}>
              {firmaError}
            </Alert>
          )}
          <TextField
            label="Nome del Firmatario *"
            fullWidth
            value={firmaNome}
            onChange={(e) => setFirmaNome(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            placeholder="Nome e cognome di chi firma"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Firma con il dito: la tastiera si chiude automaticamente.
          </Typography>
          <Box
            onPointerDown={() => { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); }}
            sx={{
              border: '2px solid #ddd',
              borderRadius: 1,
              p: 1,
              backgroundColor: 'white',
              overflowX: 'auto',
            }}
          >
            <SignatureCanvas
              ref={firmaSigCanvas}
              penColor="black"
              onBegin={() => { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); }}
              canvasProps={{
                width: 500,
                height: 200,
                className: 'sigCanvas',
                style: { touchAction: 'none' },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button
              onClick={() => firmaSigCanvas.current?.clear()}
              size="small"
            >
              Pulisci
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={handleCloseFirmaDialog}>
                Annulla
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveFirma}
                disabled={firmaLoading}
                sx={{
                  bgcolor: '#4caf50',
                  '&:hover': { bgcolor: '#388e3c' },
                  fontWeight: 600,
                }}
              >
                {firmaLoading ? 'Salvataggio...' : 'Salva Firma'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* Modal creazione rapportino standalone */}
      <RapportinoStandaloneModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          setCreateModalOpen(false);
          loadRapportini();
        }}
      />
    </Box>
  );
};

export default Rapportini;
